import { useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { MOTIVOS_DE_CANCELAMENTO } from "@bookdental/schemas";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { cn } from "@/lib/cn";

/**
 * Cancelar um serviço em produção: não volta, e a clínica vê o motivo no pedido. Respostas prontas
 * preenchem o campo (dá pra completar); sem motivo, o botão não libera.
 */
export function CancelarServicoDialog({
  aberto,
  servico,
  pendente,
  onFechar,
  onConfirmar,
}: {
  aberto: boolean;
  /** "Coroa anatômica · Fulano", pra pessoa ver o que está cancelando. */
  servico?: string;
  pendente?: boolean;
  onFechar: () => void;
  onConfirmar: (motivo: string) => void;
}) {
  const [motivo, setMotivo] = useState("");
  useEffect(() => {
    if (aberto) setMotivo("");
  }, [aberto]);
  const valido = motivo.trim().length >= 3;

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && !pendente && onFechar()}>
      <DialogContent className="max-w-md border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600" /> Cancelar serviço?
          </DialogTitle>
          <DialogDescription>
            {servico ? `${servico}. ` : ""}O serviço sai da produção e não volta. A clínica é avisada e vê o motivo no pedido.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <p className="text-xs font-medium text-zinc-600 dark:text-muted-foreground">Motivo</p>
          <div className="flex flex-wrap gap-1.5">
            {MOTIVOS_DE_CANCELAMENTO.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMotivo(m)}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                  motivo === m
                    ? "border-red-300 bg-red-50 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300"
                    : "border-zinc-200 text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground dark:hover:bg-muted",
                )}
              >
                {m}
              </button>
            ))}
          </div>
          <textarea
            aria-label="Motivo do cancelamento"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            maxLength={300}
            placeholder="Escolha uma resposta acima ou escreva o motivo..."
            className="w-full resize-none rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onFechar}
            disabled={pendente}
            className="h-9 rounded-md border border-zinc-200 px-4 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-60 dark:border-border dark:text-foreground dark:hover:bg-muted"
          >
            Voltar
          </button>
          <button
            type="button"
            disabled={!valido || pendente}
            onClick={() => onConfirmar(motivo.trim())}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-red-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-50"
          >
            {pendente && <Loader2 className="h-4 w-4 animate-spin" />}
            Cancelar serviço
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
